import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ClipboardList, HardDrive, Loader2, LogOut, Mail, TrendingUp, RefreshCw, X, Plus } from "lucide-react";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminLock, adminFetch, useAdminToken } from "@/components/AdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const FUNNEL = [
  { key: "pay_click", label: "Started checkout" },
  { key: "payment_confirmed", label: "Paid" },
  { key: "intake_submitted", label: "Submitted intake" },
];

const COMP_ORDER = ["heartlandphysics.com", "westphysics.com", "landauer.com", "upstatemp.com", "olympichp.com", "onephysics.com"];
const COMP_LABELS = {
  "heartlandphysics.com": "You",
  "westphysics.com": "West Physics",
  "landauer.com": "Landauer",
  "upstatemp.com": "Upstate MP",
  "olympichp.com": "Olympic HP",
  "onephysics.com": "One Physics",
};
const fmtWatchTime = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const Stat = ({ label, value, testid }) => (
  <div className="bg-white p-5">
    <p className="font-heading text-3xl font-extrabold tracking-tight text-blueprint" data-testid={testid}>{value}</p>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">{label}</p>
  </div>
);

const AdminAnalytics = () => {
  usePageMeta("Analytics | Heartland Physics", "Owner traffic and conversion analytics.");
  const { token, save, signOut } = useAdminToken();
  const [data, setData] = useState(null);
  const [seo, setSeo] = useState(null);
  const [seoBusy, setSeoBusy] = useState(false);
  const [seoMsg, setSeoMsg] = useState("");
  const [seoJson, setSeoJson] = useState("");
  const [terms, setTerms] = useState([]);
  const [termInput, setTermInput] = useState("");
  const [termBusy, setTermBusy] = useState(false);

  const loadSeo = () => {
    adminFetch(token, `${API}/api/admin/seo`)
      .then(async (r) => {
        if (r.status === 401) {
          signOut();
          return;
        }
        setSeo(await r.json());
      })
      .catch(() => setSeo({ configured: false, latest: null }));
    adminFetch(token, `${API}/api/admin/seo/terms`)
      .then(async (r) => {
        if (r.ok) setTerms((await r.json()).terms || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!token) return;
    adminFetch(token, `${API}/api/admin/analytics`)
      .then(async (r) => {
        if (r.status === 401) {
          signOut();
          return;
        }
        setData(await r.json());
      })
      .catch(() => setData({ days: [], total_pageviews: 0, top_pages: [], top_referrers: [], top_places: [], funnel: {} }));
    loadSeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const [emailing, setEmailing] = useState(false);
  const [emailResult, setEmailResult] = useState("");
  const [sitemapBusy, setSitemapBusy] = useState(false);

  const submitSitemap = async () => {
    setSitemapBusy(true);
    setSeoMsg("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/seo/submit-sitemap`, { method: "POST" });
      if (handle401(res)) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Submit failed");
      setSeoMsg("Sitemap submitted to Google — new pages typically get crawled within days.");
    } catch (e) {
      setSeoMsg(e.message || "Submit failed");
    }
    setSitemapBusy(false);
  };

  const emailDigest = async () => {
    setEmailing(true);
    setEmailResult("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/analytics/email-digest`, { method: "POST" });
      if (handle401(res)) return;
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Send failed");
      setEmailResult(`Sent — check ${data.to}.`);
    } catch (e) {
      setEmailResult(e.message || "Send failed");
    }
    setEmailing(false);
  };

  const handle401 = (res) => {
    if (res.status === 401) {
      signOut();
      return true;
    }
    return false;
  };

  const seoSync = async () => {
    setSeoBusy(true);
    setSeoMsg("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/seo/sync`, { method: "POST" });
      if (handle401(res)) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Sync failed — Google couldn't be reached");
      setSeoMsg(`Synced — 28-day window ending ${d.end_date} (${d.queries} queries, ${d.pages} pages).`);
      loadSeo();
    } catch (e) {
      setSeoMsg(e.message || "Sync failed");
    }
    setSeoBusy(false);
  };

  const seoSave = async () => {
    setSeoBusy(true);
    setSeoMsg("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/seo/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service_account_json: seoJson }),
      });
      if (handle401(res)) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Save failed");
      setSeoMsg(`Connected as ${d.client_email}. Now hit "Sync now" to pull your first snapshot.`);
      setSeoJson("");
      loadSeo();
    } catch (e) {
      setSeoMsg(e.message || "Save failed");
    }
    setSeoBusy(false);
  };

  const saveTerms = async (next) => {
    setTermBusy(true);
    setSeoMsg("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/seo/terms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ terms: next }),
      });
      if (handle401(res)) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Save failed");
      setTerms(d.terms || []);
    } catch (e) {
      setSeoMsg(e.message || "Save failed");
    }
    setTermBusy(false);
  };

  const addTerm = () => {
    const t = termInput.trim().toLowerCase();
    if (!t || terms.includes(t)) return;
    saveTerms([...terms, t]);
    setTermInput("");
  };

  const [watchBusy, setWatchBusy] = useState(false);

  const checkWatch = async () => {
    setWatchBusy(true);
    setSeoMsg("");
    try {
      const res = await adminFetch(token, `${API}/api/admin/seo/watch`, { method: "POST" });
      if (handle401(res)) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Check failed");
      setSeoMsg(`Competitor check complete — ${d.terms} keywords scanned.`);
      loadSeo();
    } catch (e) {
      setSeoMsg(e.message || "Check failed");
    }
    setWatchBusy(false);
  };

  if (!token) {
    return (
      <AdminLock
        onUnlock={save}
        blurb="This page is private. Enter the owner password to view site analytics."
      />
    );
  }

  const maxDay = data ? Math.max(1, ...data.days.map((d) => d.count)) : 1;

  return (
    <section data-testid="admin-analytics-page" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
        <Reveal y={12}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Internal — Site owner</p>
              <h1 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Traffic & conversions</h1>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Link to="/admin/requests" data-testid="admin-nav-requests" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <ClipboardList size={13} /> Requests
              </Link>
              <Link to="/admin/drive" data-testid="admin-nav-drive" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <HardDrive size={13} /> Drive
              </Link>
              <button onClick={signOut} data-testid="admin-logout-button" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-ink/60 transition-colors hover:border-blueprint hover:text-blueprint">
                <LogOut size={13} /> Lock
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-ink/60">
            First-party and privacy-friendly: no cookies, no personal data stored, known bots and crawlers filtered out. A visitor's IP is used once to estimate their state/region and is never saved. Last 30 days.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={emailDigest}
              disabled={emailing}
              data-testid="analytics-email-digest"
              className="flex items-center gap-2 border border-ink/25 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint disabled:opacity-60"
            >
              {emailing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
              {emailing ? "Sending…" : "Email me this report"}
            </button>
            {emailResult && <p className="text-xs text-ink/60" data-testid="analytics-email-result">{emailResult}</p>}
          </div>
        </Reveal>

        {data === null ? (
          <p className="mt-10 flex items-center gap-2 text-sm text-ink/60"><Loader2 size={16} className="animate-spin" /> Loading analytics…</p>
        ) : (
          <>
            <div className="mt-10 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-funnel">
              <Stat label="Pageviews (30 days)" value={data.total_pageviews} testid="analytics-total" />
              {FUNNEL.map((f) => (
                <Stat key={f.key} label={f.label} value={data.funnel[f.key] || 0} testid={`analytics-funnel-${f.key}`} />
              ))}
            </div>

            <div className="mt-6 border border-border bg-white p-6" data-testid="analytics-chart">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
                <BarChart3 size={13} className="text-blueprint" /> Daily pageviews
              </p>
              <div className="mt-5 flex h-32 items-end gap-1">
                {data.days.map((d) => (
                  <div
                    key={d.day}
                    className="flex-1 bg-blueprint/20 transition-colors hover:bg-blueprint"
                    style={{ height: `${Math.max(3, (d.count / maxDay) * 100)}%` }}
                    title={`${d.day}: ${d.count} pageviews`}
                  />
                ))}
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] text-ink/40">
                <span>{data.days[0]?.day}</span>
                <span>{data.days[data.days.length - 1]?.day}</span>
              </div>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="border border-border bg-white p-6" data-testid="analytics-top-pages">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Top pages</p>
                <div className="mt-3">
                  {data.top_pages.length === 0 ? (
                    <p className="text-sm text-ink/45">No visits recorded yet.</p>
                  ) : (
                    data.top_pages.map((p) => (
                      <div key={p.path} className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
                        <span className="truncate font-mono text-xs text-ink/75">{p.path}</span>
                        <span className="font-semibold text-ink">{p.count}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="border border-border bg-white p-6" data-testid="analytics-top-places">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Top regions</p>
                <div className="mt-3">
                  {data.top_places.length === 0 ? (
                    <p className="text-sm text-ink/45">No region data yet — collected from new visits going forward (bots excluded).</p>
                  ) : (
                    data.top_places.map((p) => (
                      <div key={p.place} className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
                        <span className="truncate font-mono text-xs text-ink/75">{p.place}</span>
                        <span className="font-semibold text-ink">{p.count}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="border border-border bg-white p-6" data-testid="analytics-referrers">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Top referrers</p>
                <div className="mt-3">
                  {data.top_referrers.length === 0 ? (
                    <p className="text-sm text-ink/45">No external referrers yet — this fills in as Google and others start sending visitors.</p>
                  ) : (
                    data.top_referrers.map((r) => (
                      <div key={r.ref} className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
                        <span className="truncate font-mono text-xs text-ink/75">{r.ref}</span>
                        <span className="font-semibold text-ink">{r.count}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 border border-border bg-white p-6" data-testid="analytics-seo">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
                  <TrendingUp size={13} className="text-blueprint" /> Google search rankings
                </p>
                {seo?.configured && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={seoSync}
                      disabled={seoBusy}
                      data-testid="seo-sync-button"
                      className="flex items-center gap-2 border border-ink/25 px-4 py-2 text-xs font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint disabled:opacity-60"
                    >
                      {seoBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      {seoBusy ? "Syncing…" : "Sync now"}
                    </button>
                    <button
                      onClick={submitSitemap}
                      disabled={sitemapBusy || seoBusy}
                      data-testid="seo-submit-sitemap-button"
                      title="Ask Google to recrawl the sitemap — do this after publishing new pages"
                      className="flex items-center gap-2 border border-ink/25 px-4 py-2 text-xs font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint disabled:opacity-60"
                    >
                      {sitemapBusy ? <Loader2 size={13} className="animate-spin" /> : null}
                      {sitemapBusy ? "Submitting…" : "Submit sitemap"}
                    </button>
                  </div>
                )}
              </div>
              {seoMsg && <p className="mt-3 text-xs text-ink/60" data-testid="seo-message">{seoMsg}</p>}

              {seo !== null && (
                <div className="mt-4 border-t border-border pt-4" data-testid="seo-watch-terms">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Watched keywords</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink/50">
                    These are checked against your 5 competitors in the monthly rankings email and the weekly win alerts.
                    Your top Search Console queries join automatically once connected.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {terms.map((t) => (
                      <span key={t} data-testid="seo-term" className="flex items-center gap-1.5 border border-border bg-paper px-3 py-1.5 font-mono text-xs text-ink">
                        {t}
                        <button
                          onClick={() => saveTerms(terms.filter((x) => x !== t))}
                          disabled={termBusy}
                          data-testid={`seo-term-remove-${t.replace(/\s+/g, "-")}`}
                          aria-label={`Remove ${t}`}
                          className="text-ink/40 transition-colors hover:text-red-600 disabled:opacity-40"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {terms.length === 0 && <span className="text-xs text-ink/40">No keywords yet — add your first one.</span>}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input
                      value={termInput}
                      onChange={(e) => setTermInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addTerm()}
                      placeholder="e.g. shielding plan"
                      data-testid="seo-term-input"
                      className="w-full max-w-xs border border-border bg-white px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
                    />
                    <button
                      onClick={addTerm}
                      disabled={termBusy || !termInput.trim()}
                      data-testid="seo-term-add-button"
                      className="flex items-center gap-1.5 bg-blueprint px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
                    >
                      {termBusy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                      Add
                    </button>
                  </div>
                </div>
              )}

              {seo !== null && (
                <div className="mt-6 border-t border-border pt-4" data-testid="seo-competitor-watch">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">Competitor rankings — Google top 20 (US)</p>
                    <button
                      onClick={checkWatch}
                      disabled={watchBusy}
                      data-testid="seo-watch-check-button"
                      className="flex items-center gap-2 border border-ink/25 px-4 py-2 text-xs font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint disabled:opacity-60"
                    >
                      {watchBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      {watchBusy ? "Checking Google…" : "Check now"}
                    </button>
                  </div>
                  {seo.watch_error && (
                    <p className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-700" data-testid="seo-watch-error">
                      The last automatic competitor check failed ({fmtWatchTime(seo.watch_error.at)}): {seo.watch_error.message}.
                      The table below is from the last successful run — use "Check now" to retry.
                    </p>
                  )}
                  {!seo.watch_latest ? (
                    <p className="mt-3 text-sm text-ink/55" data-testid="seo-watch-empty">
                      No competitor check yet — hit "Check now" to see where everyone ranks for your watched keywords.
                    </p>
                  ) : (
                    <>
                      <p className="mt-2 text-[11px] text-ink/45">
                        Checked {fmtWatchTime(seo.watch_latest.ran_at)} · refreshes on its own every week
                      </p>
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="seo-watch-table">
                          <thead>
                            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.14em] text-ink/45">
                              <th className="py-2 pr-3">Keyword</th>
                              {COMP_ORDER.map((d) => (
                                <th key={d} className={`px-2 py-2 text-right ${d === "heartlandphysics.com" ? "text-blueprint" : ""}`}>
                                  {COMP_LABELS[d]}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {seo.watch_latest.watch.map((w) => (
                              <tr key={w.query} className="border-b border-border last:border-b-0" data-testid="seo-watch-row">
                                <td className="py-2 pr-3 text-ink/80">{w.query}</td>
                                {COMP_ORDER.map((d) => {
                                  const p = w.positions?.[d];
                                  const isYou = d === "heartlandphysics.com";
                                  return (
                                    <td key={d} className={`px-2 py-2 text-right font-mono text-xs ${isYou ? "font-bold text-blueprint" : "text-ink/70"}`}>
                                      {p != null ? `#${p}` : "—"}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-2 text-[11px] text-ink/40">— = not in Google's top 20 for that search. Lower numbers are better — #1 is the top of Google.</p>
                    </>
                  )}
                </div>
              )}

              {seo === null ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-ink/60"><Loader2 size={14} className="animate-spin" /> Loading…</p>
              ) : !seo.configured ? (
                <div className="mt-4" data-testid="seo-setup">
                  <p className="text-sm leading-relaxed text-ink/70">
                    Connect Google Search Console to see your top search terms and watch rankings move month to month — plus a rankings email at the start of each month. One-time setup:
                  </p>
                  <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-xs leading-relaxed text-ink/60">
                    <li>In <span className="font-semibold text-ink">Google Cloud Console</span> (same project as your Drive connection): <span className="font-semibold text-ink">APIs &amp; Services → Library</span> → enable <span className="font-semibold text-ink">Google Search Console API</span>.</li>
                    <li><span className="font-semibold text-ink">IAM &amp; Admin → Service Accounts → Create service account</span> — name it heartlandphysics-gsc-reader (no project role needed).</li>
                    <li>Open the account → <span className="font-semibold text-ink">Keys → Add key → JSON</span> — download the file.</li>
                    <li>In <span className="font-semibold text-ink">Search Console → Settings → Users and permissions → Add user</span>, paste the file's client_email with <span className="font-semibold text-ink">Full</span> permission.</li>
                    <li>Paste the entire downloaded JSON file contents below and save.</li>
                  </ol>
                  <textarea
                    value={seoJson}
                    onChange={(e) => setSeoJson(e.target.value)}
                    rows={4}
                    placeholder='{"type":"service_account","project_id":"…", …}'
                    data-testid="seo-json-input"
                    className="mt-3 w-full border border-border bg-white px-3 py-2.5 font-mono text-xs text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
                  />
                  <button
                    onClick={seoSave}
                    disabled={seoBusy || seoJson.trim().length < 50}
                    data-testid="seo-save-button"
                    className="mt-2 flex items-center gap-2 bg-blueprint px-5 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
                  >
                    {seoBusy ? <Loader2 size={13} className="animate-spin" /> : null}
                    {seoBusy ? "Saving…" : "Save & connect"}
                  </button>
                  <p className="mt-2 text-[11px] text-ink/45">Stored encrypted-at-rest in your site's database, never shown again, never sent anywhere except Google.</p>
                </div>
              ) : !seo.latest ? (
                <p className="mt-4 text-sm text-ink/55" data-testid="seo-empty">
                  Connected. No snapshot yet — hit "Sync now" to pull your first 28 days of rankings.
                </p>
              ) : (
                <div className="mt-4" data-testid="seo-table">
                  <p className="text-xs text-ink/50">
                    Last 28 days ({seo.latest.start_date} → {seo.latest.end_date})
                    {seo.prev_end_date ? ` · Change vs the snapshot ending ${seo.prev_end_date}` : ""}.
                    Position is your average spot in Google — lower is better.
                  </p>
                  {seo.latest.queries.length === 0 ? (
                    <p className="mt-3 text-sm text-ink/45">No queries in this window yet — impressions build as Google indexes the new pages.</p>
                  ) : (
                    <table className="mt-3 w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.14em] text-ink/45">
                          <th className="py-2 pr-3">Query</th>
                          <th className="py-2 pr-3 text-right">Clicks</th>
                          <th className="py-2 pr-3 text-right">Impr.</th>
                          <th className="py-2 pr-3 text-right">Pos.</th>
                          <th className="py-2 text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seo.latest.queries.map((q) => (
                          <tr key={q.key} className="border-b border-border last:border-b-0" data-testid={`seo-query-row`}>
                            <td className="py-2 pr-3 text-ink/80">{q.key}</td>
                            <td className="py-2 pr-3 text-right text-ink/70">{q.clicks}</td>
                            <td className="py-2 pr-3 text-right text-ink/70">{q.impressions}</td>
                            <td className="py-2 pr-3 text-right text-ink/70">{q.position == null ? "—" : q.position.toFixed(1)}</td>
                            <td className={`py-2 text-right font-mono text-xs ${q.delta == null ? "text-ink/40" : q.delta > 0 ? "font-semibold text-blueprint" : q.delta < 0 ? "text-red-600" : "text-ink/40"}`}>
                              {q.delta == null ? "new" : q.delta > 0 ? `+${q.delta}` : q.delta}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AdminAnalytics;
